import { EmptyStateComponent } from '@components/EmptyStateComponent'
import { useCollapsibleToggle } from '@hooks/useCollapsibleToggle'
import { useCourse } from '@hooks/useCourse'
import { useEnrollment } from '@hooks/useEnrollment'
import { useMediaSize } from '@hooks/useMediaSize'
import { useModules } from '@hooks/useModules'
import { useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { View, XStack, YStack } from 'tamagui'
import { CourseContentConsumptionGhostLoading } from './CourseContentConsumptionGhostLoading'
import { DesktopScreen } from './DesktopScreen'
import { MobileLayout } from './MobileLayout'
import { useImageStatus } from '@hooks/useImageStatus'
import { useCurrentLesson } from '@hooks/useCurrentLesson'
import { useAvailableLessons } from '@hooks/useAvailableLessons'

export function CourseContentConsumption() {
  const {
    hasNextContent,
    hasPreviousContent,
    setHasNextContent,
    setHasPreviousContent,
    resetNextAndPreviousContent,
    getIfHasNextAvailableLesson,
    getIfHasPreviousAvailableLesson,
  } = useAvailableLessons()
  const mediaSize = useMediaSize()
  const isMobile = ['xs', 'sm'].includes(mediaSize)
  const { isExpanded, isHighlightedImage, reset } = useImageStatus()
  const { currentLesson } = useCurrentLesson()
  const { course } = useCourse()
  const { enrollment } = useEnrollment()
  const { modules } = useModules()
  const { t } = useTranslation('CourseDetails')
  const { isCollapsibleOpen, toggleCollapsible } = useCollapsibleToggle()

  useEffect(() => {
    if (hasNextContent !== null || !currentLesson?.id || !enrollment?.id) return
    const getData = async () => {
      const data = await getIfHasNextAvailableLesson(
        currentLesson?.id,
        enrollment?.id,
      )
      if (data?.nextAvailableLesson?.id) {
        setHasNextContent(true)
      } else {
        setHasNextContent(false)
      }
    }
    getData()
  }, [
    currentLesson?.id,
    enrollment?.id,
    getIfHasNextAvailableLesson,
    hasNextContent,
    setHasNextContent,
  ])

  useEffect(() => {
    if (hasPreviousContent !== null || !currentLesson?.id || !enrollment?.id)
      return
    const getData = async () => {
      const data = await getIfHasPreviousAvailableLesson(
        currentLesson?.id,
        enrollment?.id,
      )
      if (data?.previousAvailableLesson?.id) {
        setHasPreviousContent(true)
      } else {
        setHasPreviousContent(false)
      }
    }
    getData()
  }, [
    currentLesson?.id,
    enrollment?.id,
    getIfHasPreviousAvailableLesson,
    hasPreviousContent,
    setHasPreviousContent,
  ])

  const courseHasContent = useMemo(() => {
    if (!modules?.nodes) return false

    return modules.nodes.some((module) => {
      if (module.courseContents?.nodes.length > 0) return true
      if (module.courseModules.length > 0) {
        return module.courseModules.some(
          (submodule) => submodule.courseContents?.nodes.length > 0,
        )
      }
      return false
    })
  }, [modules])

  useEffect(() => {
    reset()
  }, [reset])

  if (!course || !enrollment || !modules) {
    return <CourseContentConsumptionGhostLoading />
  }

  if (!courseHasContent) {
    return (
      <YStack
        w="100%"
        backgroundColor="$background-100"
        testID="course-content-consumption"
      >
        <XStack h={60} w="100%" backgroundColor="$neutral-0">
          <View flex={1} marginBottom={50}>
            <EmptyStateComponent
              title={t('emptyState.title')}
              description={t('emptyState.description')}
            />
          </View>
        </XStack>
      </YStack>
    )
  }

  return (
    <YStack
      w="100%"
      backgroundColor="$background-100"
      testID="course-content-consumption"
      mt={isMobile && isHighlightedImage ? -70 : 0}
      animation="bouncy"
      transition="all 0.3s ease-in-out"
    >
      <XStack
        h={isMobile && isExpanded ? '$spacing-xs' : 60}
        w="100%"
        backgroundColor="$neutral-0"
      />

      <YStack flex={1} w="100%">
        {isMobile ? (
          <MobileLayout
            hasPreviousContent={hasPreviousContent ?? false}
            hasNextContent={hasNextContent ?? false}
            resetNextAndPreviousContent={resetNextAndPreviousContent}
          />
        ) : (
          <DesktopScreen
            hasPreviousContent={hasPreviousContent ?? false}
            hasNextContent={hasNextContent ?? false}
            isCollapsibleOpen={isCollapsibleOpen}
            toggleCollapsible={toggleCollapsible}
            resetNextAndPreviousContent={resetNextAndPreviousContent}
          />
        )}
      </YStack>
    </YStack>
  )
}
